import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { adminApi } from '@/services/api';
import Button from '@/components/ui/Button';
import { 
  BuildingStorefrontIcon, 
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface Shop {
  id: number;
  name: string;
  description: string;
  category_id: number;
  category?: {
    id: number;
    name: string;
  };
  owner_id: number;
  owner?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  is_public: boolean;
  admin_rating?: number;
  created_at: string;
  updated_at: string;
}

const ShopApprovalsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [shops, setShops] = useState<Shop[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminApi.getAllShops();
      // Filter for pending shops (not public)
      const pendingShops = response.data.filter((shop: Shop) => !shop.is_public);
      setShops(pendingShops);
    } catch (error: any) {
      let errorMessage = 'Failed to fetch shops';
      if (error?.detail) {
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          errorMessage = error.detail.map((err: any) => err.msg || err.message || String(err)).join(', ');
        } else {
          errorMessage = String(error.detail);
        }
      }
      setError(errorMessage);
      console.error('Shops fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (shopId: number) => {
    try {
      // Update shop to be public (approved)
      // Note: This might need a specific API endpoint, for now we'll use a generic update
      // You may need to add approveShop to adminApi
      await adminApi.getAllShops(); // Placeholder - would need approveShop endpoint
      fetchShops(); // Refresh list
      alert('Shop approved successfully');
    } catch (error: any) {
      console.error('Failed to approve shop:', error);
      alert('Failed to approve shop');
    }
  };

  const handleReject = async (shopId: number) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      try {
        // Note: This might need a specific API endpoint
        // You may need to add rejectShop to adminApi
        await adminApi.getAllShops(); // Placeholder - would need rejectShop endpoint
        fetchShops(); // Refresh list
        alert('Shop rejected');
      } catch (error: any) {
        console.error('Failed to reject shop:', error);
        alert('Failed to reject shop');
      }
    }
  };

  const filteredShops = shops.filter(shop =>
    shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.owner?.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.owner?.last_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading shops...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = typeof error === 'string' ? error : error?.toString() || 'An error occurred';
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading shops</h3>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <Button onClick={fetchShops}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Shop Approvals</h1>
          <p className="mt-2 text-gray-600">Review and approve pending shop submissions</p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search shops by name, description, or owner..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Shops List */}
        {filteredShops.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No pending shops</h3>
            <p className="text-gray-600">All shops have been reviewed.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredShops.map((shop) => (
              <div key={shop.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <BuildingStorefrontIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{shop.name}</h3>
                        {shop.category && (
                          <p className="text-sm text-gray-500">{shop.category.name}</p>
                        )}
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                      Pending
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{shop.description}</p>

                  {shop.owner && (
                    <div className="flex items-center space-x-2 mb-4 text-sm text-gray-600">
                      <UserIcon className="h-4 w-4" />
                      <span>
                        {shop.owner.first_name} {shop.owner.last_name}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 text-xs text-gray-500 mb-4">
                    <ClockIcon className="h-4 w-4" />
                    <span>Created: {new Date(shop.created_at).toLocaleDateString()}</span>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(shop.id)}
                      className="flex-1"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReject(shop.id)}
                      className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <XCircleIcon className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopApprovalsPage;

